import { IInitParams } from "Configuration";

import { ISectionLogger, LoggingSection } from "@multiweb/logging";
import { DIContainer } from "Global/Base/DIContainer";
import { ServiceWorkerMessageType } from "Global/Base/ServiceWorkerMessages";
import { createDefaultSectionLoggerFactory } from "Logging/defaultLogger";
import { ServiceWorkerConnector } from "OW.Remoting/Basic/ServiceWorkerConnector";
import { registerDefaultClients } from "OW.Remoting/Remoting/Clients/impl";
import { ICommunicationManager } from "OW.Remoting/Remoting/ICommunicationManager";
import { Localization } from "OW.Remoting/Remoting/Localization";
import { OWManager } from "OW.Remoting/Remoting/OWManager";
import { WorkerCommunicator } from "OW.Remoting/Remoting/WorkerCommunicator";
import { AppContext } from "./AppContext";
import { bootstrapServiceWorker } from "./bootstrapServiceWorker";
import { IAppContext } from "./IAppContext";

/**
 * Konfigurační možnosti řídící proces bootstrapu
 */
export interface IBootstrapOptions {
    /** Umožňuje předat custom nastavení aplikace. Pokud není předáno, tak se ho pokusí načíst ze standardního umístění. */
    initParams?: IInitParams;
    /** Oblasti aplikace, které se mají ziniciovat. Pokud je parametr `undefined`, tak se ziniciuje vše, jinak jen vyjmenované oblasti. */
    initAreas?: IBootstrappedAreas;
    /** Nastavení pro Service Worker. Pokud je parametr `undefined`, tak je aktivováno vše, jinak jen vyjmenované funkcionality.  */
    serviceWorkerOptions?: IBootstrapServiceWorkerOptions;
}

/**
 * Oblasti, které se mají zinicializovat během bootstrapu
 */
export interface IBootstrappedAreas {
    /** Konfigurace komunikace s lokalizační službou. Nastartování sledování a automatické lokalizace html elementů. */
    localizations?: boolean;
    /** Konfigurace komunikace s aplikační webovou službou. */
    communication?: boolean;
    /** Registrace a konfigurace service workeru a napojení na něj. */
    serviceWorker?: boolean;
}

/**
 * Oblasti, které se mají zinicializovat během bootstrapu Service Workeru
 */
export interface IBootstrapServiceWorkerOptions {
    /** Callback spuštěný v okamžiku nalezení updatu SW */
    onUpdateFound?: () => Promise<void>;
    /** Callback spuštěný v okamžiku připraveného updatu SW */
    onUpdateReady?: () => Promise<void>;
    /** Posílání udržovacích dotazů */
    heartBeat?: boolean;
}

/**
 * Ziniciuje kontext aplikace a nakonfiguruje různé oblasti aplikace (komunikace s webovou službou, lokalizace, service worker, ...).
 * @param options Volitelné možnosti řídící proces bootstrapu
 */
export async function bootstrapApp(options?: IBootstrapOptions): Promise<IAppContext> {
    const { initParams, initAreas, serviceWorkerOptions } = options || {} as IBootstrapOptions;

    const context = AppContext.instance;
    await context.initialize(initParams);

    // Pozn. Záměrně se zde ještě nevkládá loggerFactory do DIContaineru ani neinicialiyzuje LazyLoadingSectionLogger.
    //       Důvod: Nejsou ještě k dispozici custom implementace.
    //       Proto se toto děje až v MainForm.
    // Logovat se potřebuje už teď. Pak se ale musí použít defaultní logger.
    const defaultLoggerFactory = createDefaultSectionLoggerFactory(context.configuration.logging);

    WorkerCommunicator.initialize(defaultLoggerFactory.create(LoggingSection.workerCommunicator));

    if (!initAreas || initAreas.serviceWorker) {
        const serviceWorkerLogger = defaultLoggerFactory.create(LoggingSection.serviceWorker);
        // počkat na doběhnutí inicializace SW před pokračováním (aby už běžel a mohl zachytit požadavky z inicializace dalších součástí)
        await initializeServiceWorker(context, serviceWorkerLogger, serviceWorkerOptions);
    }

    const initializations: Promise<void>[] = [];
    if (!initAreas || initAreas.communication) {
        registerDefaultClients(context.addresses.serviceUrl, context.configuration.getDevelopmentOption("webServiceVersion"));
        OWManager.initialize(DIContainer.instance.create(ICommunicationManager), defaultLoggerFactory.create(LoggingSection.rights));
    }

    if (!initAreas || initAreas.localizations)
        initializations.push(Localization.initialize(context.addresses.serviceUrl, context.appId));

    await Promise.all(initializations);

    return context;
}

const initializeServiceWorker = async (context: IAppContext, serviceWorkerLogger: ISectionLogger, options?: IBootstrapServiceWorkerOptions) => {
    const onActivated = (connector: typeof ServiceWorkerConnector) => {
        if (options?.heartBeat) {
            if (context.configuration.heartBeatInterval) {
                const intervalTime = 1000 * 60 * context.configuration.heartBeatInterval;
                self.setInterval(() => connector.sendMessage({ type: ServiceWorkerMessageType.alive }), intervalTime);
            }
        }

        connector.sendMessage({ type: ServiceWorkerMessageType.updateData });
    };

    const onError = (error: any) => {
        serviceWorkerLogger.error("Registration failed: ", error.toString());

        if (error.name === "SecurityError" && error.message.includes("An SSL certificate error occurred"))
            alert("Connection is not trusted! Service worker is disabled and the application will not work correctly. Contact your administrator.");

        // bug Firefoxu https://bugzilla.mozilla.org/show_bug.cgi?id=1413615
        if (error instanceof DOMException && error.name === "SecurityError" && error.message === "The operation is insecure.")
            location.href = "browser-settings.html";
    };

    await bootstrapServiceWorker(
        onActivated,
        options?.onUpdateFound ?? (async () => { /* Prázdná arrow funkce */ }),
        options?.onUpdateReady ?? (async () => { /* Prázdná arrow funkce */ }),
        onError,
        serviceWorkerLogger,
    );
};
